import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from '@mui/material';
import { Study, Participant } from '../../../types';
import { apiService } from '../../../config/api';
import { StudyDeletionJob, studyDeletionService, participantDeletionService } from '../../../services/studyDeletionService';
import DeletionConfirmation from './DeletionConfirmation';

const steps: Record<StudyDeletionJob['step'], string> = {
  inventory: 'Stopping collection and preparing cleanup', database: 'Removing collected Fitbit data',
  legacy: 'Removing other collected data and consent records', cloud: 'Finishing active exports and removing cloud files',
  exports: 'Removing extraction jobs', records: 'Removing participants and study records', completed: 'Study deleted',
};
interface Callbacks { onClose: () => void; onAccepted: (job: StudyDeletionJob) => void; onDeleted: () => void }
export default function StudyDeletionDialog({ study, ...callbacks }: Callbacks & { study: Study }) {
  return <DeletionDialog key={study.id} target={study} confirmationValue={study.name} kind="study" service={studyDeletionService} {...callbacks} />;
}
export function ParticipantDeletionDialog({ participant, ...callbacks }: Callbacks & { participant: Participant }) {
  const name = [participant.firstName, participant.lastName].filter(Boolean).join(' ') || participant.trackingId || participant.participantId;
  return <DeletionDialog key={participant.id || participant.participantId} target={{ id: participant.id || participant.participantId, name, deletion: participant.deletion }}
    confirmationValue={participant.participantId || participant.id} kind="participant" service={participantDeletionService} {...callbacks} />;
}
function DeletionDialog({ target, confirmationValue, kind, service, onClose, onAccepted, onDeleted }: Callbacks & {
  target: { id: string; name: string; deletion?: unknown }; confirmationValue: string; kind: 'study' | 'participant'; service: typeof studyDeletionService;
}) {
  const [job, setJob] = useState<StudyDeletionJob | null>(null);
  const [accepted, setAccepted] = useState(Boolean(target.deletion));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const confirmed = Boolean(confirmationValue) && confirmation === confirmationValue;
  const token = async () => { const value = await apiService.getAuthToken(); if (!value) throw new Error('Please sign in again.'); return value; };
  useEffect(() => {
    if (!accepted) return;
    let stopped = false, timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const result = await service.get(target.id, await token());
        if (stopped) return;
        if (!result.deletion) throw new Error('Could not find this deletion. Please refresh.');
        setJob(result.deletion); setError('');
        if (result.deletion.status === 'completed') { onDeleted(); return; }
      } catch (err) { if (!stopped) setError(err instanceof Error ? err.message : 'Could not refresh deletion progress.'); }
      if (!stopped) timer = setTimeout(refresh, 5000);
    };
    void refresh();
    return () => { stopped = true; clearTimeout(timer); };
  }, [accepted, target.id, service, onDeleted]);
  const submit = async () => {
    if (busy || !confirmed) return;
    setBusy(true); setError('');
    try {
      const result = await (job?.status === 'failed' ? service.retry : service.request)(target.id, await token());
      if (!result.deletion) throw new Error('Deletion was not accepted.');
      setJob(result.deletion); setAccepted(true); setConfirmation(''); onAccepted(result.deletion);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not start deletion.'); }
    finally { setBusy(false); }
  };
  const failed = job?.status === 'failed';
  return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle>{accepted ? (failed ? 'Deletion needs attention' : `Deleting ${kind}`) : `Delete “${target.name}”?`}</DialogTitle>
    <DialogContent>
      {!accepted ? <>
        <Typography>{kind === 'participant'
          ? 'This removes this participant from the study, stops their collection, and deletes their collected data and extraction files in Google Cloud. They will also be removed from daily extraction schedules. Other participants and the study stay unchanged.'
          : 'This removes the study, its participants, collected data, daily extraction schedules and files in its Google Cloud bucket.'}</Typography>
        <Typography sx={{ mt: 2 }}>Deletion cannot be undone. Previously downloaded or emailed files are not removed. Cloud retention and backups may keep copies until they expire.</Typography>
      </> : <Box role="status">
        <Typography>{job ? (kind === 'participant' && job.step === 'records' ? 'Removing participant records' : steps[job.step]) : 'Loading deletion progress…'}</Typography>
        {!failed && <LinearProgress sx={{ my: 2 }} aria-label={`${kind === 'participant' ? 'Participant' : 'Study'} deletion in progress`} />}
        {job?.step === 'database' && job.progress && <Typography variant="body2">
          {job.progress.rangesDone} of {job.progress.rangesTotal} time ranges removed.
          {job.progress.phase !== 'measurements' && ' Checking collection records.'}
        </Typography>}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>You can close this window. Deletion continues in the background; open it again from {kind === 'participant' ? 'the participant’s Actions column' : 'the study card'}.</Typography>
      </Box>}
      {(!accepted || failed) && <DeletionConfirmation key={confirmationValue} kind={kind} expectedValue={confirmationValue}
        value={confirmation} onChange={setConfirmation} disabled={busy} />}
      {(error || job?.error) && <Alert severity="error" sx={{ mt: 2 }}>{error || job?.error}</Alert>}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={busy}>{accepted ? 'Close' : 'Cancel'}</Button>
      {(!accepted || failed) && <Button variant="contained" color="error" onClick={() => void submit()} disabled={busy || !confirmed}>
        {busy ? 'Please wait…' : failed ? 'Retry deletion' : `Delete ${kind}`}
      </Button>}
    </DialogActions>
  </Dialog>;
}
